import type { PartnerSearch } from "@cabocheck/shared";
import { getDeviceLocale } from "@cabocheck/shared";
import type { LoaderFunctionArgs } from "react-router";
import { Link, useLoaderData } from "react-router";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { MailIcon, PhoneIcon, RouteIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import benefit1 from "~/assets/images/microsite/benefit1.png";
import benefit2 from "~/assets/images/microsite/benefit2.png";
import benefit3 from "~/assets/images/microsite/benefit3.png";
import benefit4 from "~/assets/images/microsite/benefit4.png";
import benefitHero1 from "~/assets/images/microsite/benefit_hero_1_replacement.jpeg";
import benefitHero2 from "~/assets/images/microsite/benefit_hero_2.jpeg";
import logoSmall from "~/assets/logos/logo-small.svg";
import { Button } from "~/components/ui/button";
import { AblaufComponent } from "~/features/microsite/Ablauf";
import { FAQComponent } from "~/features/microsite/FAQs";
import { MicrositeButton } from "~/features/microsite/MicrositeButton";
import { TestOffersComponent } from "~/features/microsite/TestOffers";
import { PublicFooter } from "~/features/public/Footer";
import { PHARMACY_SEARCH_ENABLED } from "~/feature_flags";
import { PublicPocProductBox } from "~/features/poc/PublicPocProductBox";
import { MedicalTestProfileBox } from "~/features/tests/MedicalTestProfileBox";
import { loadFAQs } from "~/utils/apis/loadFAQs";
import { loadPublicProducts } from "~/utils/apis/loadPublicProducts.server";
import { loadProducts } from "~/utils/apis/loadProducts.server";

export async function loader({ request }: LoaderFunctionArgs) {
  const device_locale = getDeviceLocale(request);
  const [products, pocProducts] = await Promise.all([
    loadProducts(device_locale),
    loadPublicProducts({ device_locale, product_type: "TEST_KIT" }),
  ]);

  const faqs = loadFAQs(device_locale).then((r) => r.filter((faq) => faq.available_for_users));

  return {
    products,
    pocProducts,
    faqs: await faqs,
  };
}

export default function PharmacyHome() {
  const { t } = useTranslation();
  const data = useLoaderData<typeof loader>();

  return (
    <div>
      <section id="start">
        <Hero />
      </section>
      <section id="vorteile" className="public-container">
        <h1 className="mb-10 mt-20 text-center text-5xl font-bold sm:text-6xl">
          {t("Ihre Vorteile bei der Blutanalyse in der Apotheke")}
        </h1>
        <Benefits />
        <FeatureOverview />
      </section>

      <section id="angebot">
        <TestOffersComponent
          labProducts={data.products?.slice(0, 6).map((p) => (
            <MedicalTestProfileBox
              key={p.medical_test_profile_guid}
              to={`products/${p.name}`}
              panel={{
                name: p.name,
                subline: p.subline,
                medical_tests: p.medical_tests,
              }}
            />
          ))}
          pocProducts={data.pocProducts?.slice(0, 6).map((product) => (
            <PublicPocProductBox
              key={product.product_guid}
              product={product}
              to={`poc-products/${product.product_guid}`}
            />
          ))}
        />
      </section>
      <section id="ablauf">
        <AblaufComponent />
      </section>
      {PHARMACY_SEARCH_ENABLED ? (
        <section id="apothekeFinden">
          <AquireTest />
        </section>
      ) : null}
      <section id="faq">
        <FAQComponent items={data.faqs} showTypeTabs />
      </section>

      <PublicFooter />
    </div>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <div className="-mt-24 grid overflow-hidden bg-gradient-to-r from-pink-100 to-blue-100 pt-44 md:min-h-[60vh]">
      <div className="public-container grid items-center justify-between gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-3 px-8 md:items-start">
          <h1 className="mb-4 mt-10 animate-fade-scale-in text-center text-5xl font-bold md:mt-0 md:text-left lg:text-7xl">
            {t("Gesundheit beginnt mit einem Test")}
          </h1>
          <MicrositeButton asChild>
            <Link to="#vorteile">{t("Mehr erfahren")}</Link>
          </MicrositeButton>
          <MicrositeButton asChild variant="red">
            <Link to="https://mein.cabocheck.com">{t("Testergebnis abrufen")}</Link>
          </MicrositeButton>
        </div>
        <div className="relative flex items-center self-stretch">
          <div className="relative p-14 md:p-7 lg:p-10">
            <img
              src="https://cdn.prod.website-files.com/6676ea84737cc38e6b4276e4/669a41d146775ba6fa8353e5_firefly-pharmacist-2-p-500.png"
              alt=""
              className="mb-8 animate-fade-scale-in rounded-lg"
            />
            <div className="absolute -bottom-2 left-0 right-0 h-44 bg-white blur-xl sm:bottom-2 md:h-32" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Benefits() {
  const { t } = useTranslation();

  return (
    <div className="mb-16 mt-10 grid gap-6 text-center sm:grid-cols-2 md:grid-cols-4">
      <div className="flex flex-col justify-center space-y-6 rounded-3xl bg-gray-50 p-4 py-6">
        <img src={benefit2} alt="" className="mx-auto w-16" />
        <h4 className="text-lg font-semibold">{t("Keine lange Wartezeiten")}</h4>
      </div>
      <div className="flex flex-col justify-center space-y-6 rounded-3xl bg-gray-50 p-4 py-6">
        <img src={benefit4} alt="" className="mx-auto w-16" />
        <h4 className="text-lg font-semibold">{t("Ergebnisse als PDF per Email oder online abrufbar")}</h4>
      </div>
      <div className="space-y-6 rounded-3xl bg-gray-50 p-4 py-8">
        <img src={benefit1} alt="" className="mx-auto w-16" />
        <h4 className="text-lg font-semibold">
          {t("Einfache Blutentnahme. Bei CaboLab mittels speziellem Unterdruckgerät.")}
        </h4>
        <b className="text-sm font-semibold">({t("nur 2 Minuten")})</b>
      </div>
      <div className="flex flex-col justify-center space-y-6 rounded-3xl bg-gray-50 p-4 py-6">
        <img src={benefit3} alt="" className="mx-auto w-16" />
        <h4 className="text-lg font-semibold">{t("Auswertung durch medizinisches Facharztlabor bei CaboLab")}</h4>
      </div>
    </div>
  );
}

function FeatureOverview() {
  const { t } = useTranslation();

  return (
    <>
      <FeatureSection
        title={t("Warum Bluttests wichtig sind")}
        image={
          <div className="self-stretch overflow-hidden rounded-3xl bg-gray-100">
            <img src={benefitHero1} alt="" className="h-full w-full rounded-t-3xl object-cover" />
          </div>
        }
      >
        <FeatureItem
          title={t("Erste Anhaltspunkte für Gesundheitsrisiken")}
          description={t(
            "Bluttests können Risikofaktoren und mögliche Erkrankungen aufzeigen, wie Blutzucker und Cholesterinspiegel.",
          )}
          icon={logoSmall}
        />
        <FeatureItem
          title={t("Wann zum Arzt gehen?")}
          description={t(
            "Bei Abweichungen der Referenzbereiche, empfehlen wir Ihnen eine Ärztliche Beratung. Medizinische Diagnosen dürfen nur durch Ärzte gestellt werden.",
          )}
          icon={logoSmall}
        />
      </FeatureSection>
      <FeatureSection
        title={t("Gesundheit und Ernährung im Einklang")}
        image={
          <div className="self-stretch overflow-hidden rounded-3xl bg-gray-100">
            <img src={benefitHero2} alt="" className="h-full w-full rounded-t-3xl object-cover" />
          </div>
        }
        isFlipped
      >
        <div className="pb-16">
          <p className="text-lg text-gray-600">
            {t(
              "Eine Ernährungsumstellung ist oft der erste Schritt hin zu einer gesünderen Lebensweise. Genauso wichtig ist es, seine körperliche Gesundheit im Blick zu haben.",
            )}
          </p>
          <p className="mt-8 text-lg text-gray-600">
            {t(
              "Deshalb bieten wir eine Vielzahl an Tests an, um Ihre Gesundheit umfassend zu überwachen und zu verbessern.",
            )}
          </p>
        </div>
      </FeatureSection>
    </>
  );
}

function FeatureSection({
  title,
  image,
  children,
  isFlipped,
}: {
  title: string;
  image: React.ReactNode;
  children: React.ReactNode;
  isFlipped?: boolean;
}) {
  const FeatureList = () => (
    <div className="space-y-6 pb-20 pt-20">
      <h2 className="text-4xl font-bold">{title}</h2>

      {children}
    </div>
  );

  return (
    <div className="mb-10 px-4 py-8">
      <div className={classNames(`grid grid-cols-1 items-center gap-8 lg:grid-cols-2`)}>
        <div className={classNames({ "lg:order-last": isFlipped })}>
          <FeatureList />
        </div>
        {image}
      </div>
    </div>
  );
}

function FeatureItem({ title, description, icon }: { title: string; description: string; icon: string }) {
  return (
    <div className="flex items-start gap-4">
      <div>
        <img src={icon} alt="" className="aspect-square w-10" />
      </div>
      <div className="flex-1">
        <h3 className="text-xl font-semibold">{title}</h3>
        <p className="text-lg text-gray-600">{description}</p>
      </div>
    </div>
  );
}

function AquireTest() {
  const { t } = useTranslation();
  const [zip, setZip] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["search_partners", { zip }],
    enabled: !!zip,
    retry: false,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("zip", zip);

      const response = await fetch(`/api/v1/partners/search?${searchParams.toString()}`, {
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();

      return {
        data: (data?.response?.partners ?? []) as PartnerSearch[],
        status: response.status,
      };
    },
  });

  return (
    <div className="public-container">
      <div className="grid gap-12 rounded-3xl bg-gray-100 py-10 md:px-10">
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl p-9 text-center">
          <h3 className="text-4xl font-bold tracking-tighter">{t("Wie kann ich einen Test erwerben?")}</h3>
          <p className="text-gray-600">
            {t("Bitte tragen Sie ihre PLZ ein. Wir informieren Sie über die CaboCheck Apotheken in Ihrer Nähe.")}
          </p>

          {data ? (
            data.status === 403 ? (
              <p className="text-gray-600">{t("Sie haben die Rate Limit für die Anfragen überschritten!")}</p>
            ) : data.data.length > 0 ? (
              <div className="grid gap-4 pt-4 sm:grid-cols-3 lg:grid-cols-4">
                {data.data.map((partner) => (
                  <PartnerCard key={partner.partner_guid} {...partner} />
                ))}
              </div>
            ) : (
              <p className="text-gray-600">{t("Es gibt keine CaboCheck Apotheken in Ihrer Nähe!")}</p>
            )
          ) : (
            <form
              className="mt-4 grid gap-4 self-stretch"
              onSubmit={(e) => {
                e.preventDefault();
                setZip(e.currentTarget.zip.value);
              }}
            >
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <label htmlFor="partner-zip" className="sr-only">
                  {t("PLZ")}
                </label>
                <input
                  id="partner-zip"
                  type="text"
                  name="zip"
                  placeholder="PLZ"
                  className="rounded-xl border p-3 sm:w-40"
                  required
                  maxLength={5}
                  minLength={5}
                  disabled={isLoading}
                />
                <MicrositeButton disabled={isLoading}>{t("Apotheke suchen")}</MicrositeButton>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export function PartnerCard({
  name,
  city,
  email,
  logo_url,
  short_url,
  street,
  telephone,
  zip,
  distance,
}: PartnerSearch) {
  const { t } = useTranslation();
  const address = `${street}, ${zip} ${city}`;
  const website = short_url;

  return (
    <div className="flex flex-col items-stretch justify-stretch gap-2 break-words rounded-xl bg-white p-4 text-xs">
      {logo_url ? (
        <div className="shrink-0 self-center">
          <img src={logo_url} alt={`${name} logo`} className="h-20 w-auto object-contain" />
        </div>
      ) : (
        <div className="h-2" />
      )}

      <h5 className="text-navy-900 font-bold">{name}</h5>
      <p className="text-sm text-gray-700">{address}</p>

      {telephone && (
        <a href={`tel:${telephone}`} className="text-blue-600 hover:underline">
          <PhoneIcon aria-hidden="true" className="mb-0.5 mr-1 inline-block h-3 w-3" />
          {telephone}
        </a>
      )}

      {email && (
        <a href={`mailto:${email}`} className="text-blue-600 hover:underline">
          <MailIcon aria-hidden="true" className="mb-0.5 mr-1 inline-block h-3 w-3" />
          {email}
        </a>
      )}
      <p className="flex-1 text-sm text-gray-700">
        <RouteIcon aria-hidden="true" className="mb-0.5 mr-1 inline-block h-3 w-3" />
        {distanceFormatter(distance)} {t("entfernung")}
      </p>
      {website && (
        <Button variant="outline" asChild>
          <Link to={website} target="_blank" rel="noopener noreferrer">
            {t("Apotheke besuchen")}
          </Link>
        </Button>
      )}
    </div>
  );
}

function distanceFormatter(distance: number) {
  if (distance < 1000) {
    return `${distance}m`;
  }
  return `${(distance / 1000).toFixed(2)}km`;
}
